import {
  analyticsSubjectIdSchema,
  productEventSchema,
  sanitizeProductProperties,
  type CaptureOptions,
  type ProductProperties,
} from '@galapagos/product-analytics';

import type { AnalyticsProvider, AnalyticsTransport } from '../types';

export type AnalyticsPolicyRejection =
  | 'invalid_event'
  | 'invalid_properties'
  | 'invalid_subject'
  | 'secret_property';

export interface AnalyticsPolicyHooks {
  onReject?(reason: AnalyticsPolicyRejection): void;
}

const warnedRejections = new Set<AnalyticsPolicyRejection>();

function warnRejectedAnalytics(reason: AnalyticsPolicyRejection): void {
  if (warnedRejections.has(reason)) return;
  warnedRejections.add(reason);
  console.warn(`[product-analytics] dropped ${reason.replace('_', ' ')}`);
}

/**
 * The single product-policy boundary used by every PostHog concrete.
 * Invalid data is rejected before vendor code and never breaks product behavior.
 */
export function guardProductAnalytics(
  transport: AnalyticsTransport,
  hooks: AnalyticsPolicyHooks = {},
): AnalyticsProvider {
  let hasSubject = false;
  const onReject = hooks.onReject ?? warnRejectedAnalytics;

  return {
    track: (event, properties, options) => {
      const parsed = productEventSchema.safeParse({ event, properties, options });
      if (!parsed.success) {
        onReject('invalid_event');
        return;
      }

      let sanitized;
      try {
        sanitized = sanitizeProductProperties(parsed.data.properties);
      } catch {
        onReject('invalid_properties');
        return;
      }
      if (sanitized.removedSecretCount > 0) onReject('secret_property');
      const guardedOptions: CaptureOptions = {
        ...parsed.data.options,
        ...(!hasSubject ? { processPersonProfile: false } : {}),
      };
      transport.track(parsed.data.event, sanitized.properties, guardedOptions);
    },
    setUser: (subjectId, properties) => {
      const parsed = analyticsSubjectIdSchema.safeParse(subjectId);
      if (!parsed.success) {
        onReject('invalid_subject');
        return;
      }
      let sanitizedProperties: ProductProperties | undefined;
      if (properties) {
        try {
          const sanitized = sanitizeProductProperties(properties);
          sanitizedProperties = sanitized.properties;
          if (sanitized.removedSecretCount > 0) onReject('secret_property');
        } catch {
          onReject('invalid_properties');
          return;
        }
      }
      hasSubject = true;
      transport.setUser(parsed.data, sanitizedProperties);
    },
    clearUser: () => {
      hasSubject = false;
      transport.clearUser();
    },
    flush: () => transport.flush?.(),
  };
}
